/**
 * Room merge execution — shared by the room-mode merge tool (Enter on a
 * previewed selection) and the left panel's multi-select context menu.
 *
 * Per connected group of adjacent rooms, the largest source room keeps its
 * identity (name, color, tag folder, structure links) and receives the merged
 * polygon via the `room_updated` persistence path; the other source rooms are
 * deleted (`measurement_delete` — the delete cascade dissolves their tag
 * folders and updates opening links). Rooms merge only within their own
 * floor: stacked floors can overlap in plan.
 */

import * as THREE from 'three';
import type { Viewer } from '../viewer/viewer';
import { PointoramaRoom } from './PointoramaRoom';
import { mergeRoomPolygons } from './WallRegionBuilder';

/** Default for merge paths without a thickness setting (the context menu). */
export const DEFAULT_MERGE_WALL_THICKNESS_M = 0.3;

/**
 * Merge every connected group of adjacent rooms in `rooms` (walls up to
 * `maxWallThickness` apart count as adjacent). Returns the rooms that
 * survived as merge results, or an empty array when nothing was mergeable —
 * in that case the scene and selection are left untouched.
 */
export function mergeRoomsInScene(viewer: Viewer, rooms: PointoramaRoom[], maxWallThickness: number = DEFAULT_MERGE_WALL_THICKNESS_M): PointoramaRoom[] {
  if (rooms.length < 2) return [];

  const byFloor = new Map<string, PointoramaRoom[]>();
  for (const room of rooms) {
    const floorId = room.groupIdentifier ?? '';
    const floorRooms = byFloor.get(floorId);
    if (floorRooms) {
      floorRooms.push(room);
    } else {
      byFloor.set(floorId, [room]);
    }
  }

  // Plan all merges before touching anything, so a nothing-to-merge call has
  // no side effects (no selection clearing, no writes).
  const planned: Array<{ members: PointoramaRoom[]; outerRing: THREE.Vector2[] }> = [];
  for (const floorRooms of byFloor.values()) {
    if (floorRooms.length < 2) continue;
    const byId = new Map<string, PointoramaRoom>(floorRooms.map((r) => [r.identifier, r]));
    const groups = mergeRoomPolygons(
      floorRooms.map((r) => ({ identifier: r.identifier, polygon: r.getPolygon() })),
      maxWallThickness,
    );
    for (const group of groups) {
      const members = group.roomIdentifiers.map((id) => byId.get(id)).filter((r): r is PointoramaRoom => !!r);
      if (members.length >= 2) planned.push({ members, outerRing: group.outerRing });
    }
  }
  if (planned.length === 0) return [];

  // Clear the Redux selection before mutating — deleting still-selected
  // rooms would leave stale entries behind.
  viewer.scene.dispatchEvent({
    type: 'measurement_select',
    value: { measure: null, isCtrl: false },
  });

  const keptRooms: PointoramaRoom[] = [];
  for (const { members, outerRing } of planned) {
    const kept = members.reduce((a, b) => (b.getArea() > a.getArea() ? b : a));
    kept.setPolygon(outerRing.map((v) => new THREE.Vector3(v.x, v.y, baseHeightAt(kept, v.x, v.y))));
    viewer.scene.dispatchEvent({ type: 'room_updated', room: kept });

    for (const member of members) {
      if (member === kept) continue;
      viewer.scene.removeRoom(member);
      viewer.scene.dispatchEvent({
        type: 'measurement_delete',
        value: { measure: { identifier: member.identifier } },
      });
    }

    // Leave the merged room selected so the result stays highlighted.
    viewer.scene.dispatchEvent({
      type: 'measurement_select',
      value: { measure: kept, isCtrl: true },
    });
    keptRooms.push(kept);
  }
  return keptRooms;
}

/** Solve the room's base plane for z at (x, y) so the merged polygon sits on
 *  the same (possibly tilted) base as the room that keeps its identity. */
function baseHeightAt(room: PointoramaRoom, x: number, y: number): number {
  const { position: p, normal: n } = room.getBase();
  if (Math.abs(n.z) > 1e-6) {
    return p.z - (n.x * (x - p.x) + n.y * (y - p.y)) / n.z;
  }
  const first = room.getPolygon()[0];
  return first ? first.z : p.z;
}
